import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CaretLeft, CaretRight, CalendarBlank, CircleNotch, Trash, StackPlus } from "@phosphor-icons/react";
import { format, addDays, startOfWeek } from "date-fns";
import { es } from "date-fns/locale";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

export default function Horarios() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [schedules, setSchedules] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [cell, setCell] = useState(null); // {employee, date, assignment}
  const [assignForm, setAssignForm] = useState({ shift_id: "", observation: "" });
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ shift_id: "", observation: "", overwrite: true });
  const [bulkEmps, setBulkEmps] = useState({});
  const [bulkDays, setBulkDays] = useState({});
  const [bulkRange, setBulkRange] = useState({ from: "", to: "" });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [fs, setFs] = useState(null);
  const [fsSaving, setFsSaving] = useState(false);

  const WEEKDAYS = [
    { d: 1, label: "Lun" }, { d: 2, label: "Mar" }, { d: 3, label: "Mié" },
    { d: 4, label: "Jue" }, { d: 5, label: "Vie" }, { d: 6, label: "Sáb" }, { d: 0, label: "Dom" },
  ];

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const startISO = format(weekStart, "yyyy-MM-dd");
  const endISO = format(addDays(weekStart, 6), "yyyy-MM-dd");

  const load = useCallback(() => {
    setSchedules(null);
    localApi.get("/fixed-schedule").then((r) => setFs(r.data)).catch(() => {});
    Promise.all([
      api.get("/schedules", { params: { start: startISO, end: endISO } }),
      api.get("/employees"),
      api.get("/shifts"),
    ])
      .then(([sch, emp, shi]) => {
        setSchedules(Array.isArray(sch.data) ? sch.data : []);
        setEmployees((Array.isArray(emp.data) ? emp.data : []).filter((e) => e.active));
        setShifts(Array.isArray(shi.data) ? shi.data : []);
      })
      .catch((e) => {
        toast.error(apiError(e));
        setSchedules([]);
      });
  }, [startISO, endISO]);

  useEffect(load, [load]);

  const byCell = useMemo(() => {
    const map = {};
    (schedules || []).forEach((s) => {
      map[`${s.employee_id}|${String(s.date).slice(0, 10)}`] = s;
    });
    return map;
  }, [schedules]);

  const openCell = (emp, date) => {
    const key = `${emp.id}|${format(date, "yyyy-MM-dd")}`;
    const assignment = byCell[key];
    setCell({ employee: emp, date, assignment });
    setAssignForm({ shift_id: assignment?.shift_id || "", observation: assignment?.observation || "" });
  };

  const saveCell = async () => {
    if (!assignForm.shift_id) {
      toast.error("Selecciona un turno");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee_id: cell.employee.id,
        shift_id: assignForm.shift_id,
        date: format(cell.date, "yyyy-MM-dd"),
        observation: assignForm.observation,
      };
      if (cell.assignment && cell.assignment.shift_id === assignForm.shift_id) {
        await api.patch(`/schedules/${cell.assignment.id}/observation`, { observation: assignForm.observation });
      } else {
        if (cell.assignment) await api.delete(`/schedules/${cell.assignment.id}`);
        await api.post("/schedules", payload);
      }
      toast.success("Horario guardado");
      setCell(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeCell = async () => {
    setSaving(true);
    try {
      await api.delete(`/schedules/${cell.assignment.id}`);
      toast.success("Asignación eliminada");
      setCell(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openBulk = () => {
    setBulkForm({ shift_id: "", observation: "", overwrite: true });
    setBulkEmps(Object.fromEntries(employees.map((e) => [e.id, true])));
    setBulkDays({ 1: true, 2: true, 3: true, 4: true, 5: true, 6: false, 0: false });
    setBulkRange({ from: startISO, to: endISO });
    setBulkOpen(true);
  };

  const bulkTargetDates = () => {
    const dayNums = Object.keys(bulkDays).filter((k) => bulkDays[k]).map(Number);
    if (!bulkRange.from || !bulkRange.to || !dayNums.length) return [];
    const dates = [];
    let cur = new Date(bulkRange.from + "T12:00:00");
    const endD = new Date(bulkRange.to + "T12:00:00");
    while (cur <= endD && dates.length < 370) {
      if (dayNums.includes(cur.getDay())) dates.push(format(cur, "yyyy-MM-dd"));
      cur = addDays(cur, 1);
    }
    return dates;
  };

  const bulkSave = async () => {
    const empIds = Object.keys(bulkEmps).filter((k) => bulkEmps[k]);
    const dates = bulkTargetDates();
    if (!bulkForm.shift_id) { toast.error("Selecciona un turno"); return; }
    if (!empIds.length) { toast.error("Selecciona al menos un empleado"); return; }
    if (!dates.length) { toast.error("El rango no incluye los días seleccionados"); return; }
    if (dates.length * empIds.length > 400) { toast.error("Demasiadas asignaciones de una vez (máx. 400). Reduce el rango o los empleados."); return; }
    setBulkSaving(true);
    try {
      let replaced = 0;
      if (bulkForm.overwrite) {
        const { data: existing } = await api.get("/schedules", { params: { start: bulkRange.from, end: bulkRange.to } });
        const delIds = (Array.isArray(existing) ? existing : [])
          .filter((s) => empIds.includes(s.employee_id) && dates.includes(String(s.date).slice(0, 10)))
          .map((s) => s.id);
        if (delIds.length) {
          await api.post("/schedules/bulk-delete", { ids: delIds });
          replaced = delIds.length;
        }
      }
      const jobs = [];
      empIds.forEach((eid) => dates.forEach((dt) => jobs.push({ employee_id: eid, date: dt })));
      const results = await Promise.allSettled(jobs.map((j) => api.post("/schedules", {
        employee_id: j.employee_id,
        shift_id: bulkForm.shift_id,
        date: j.date,
        observation: bulkForm.observation,
      })));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok) toast.success(`${ok} asignaciones creadas${replaced ? ` · ${replaced} reemplazadas` : ""}${fail ? ` · ${fail} fallaron` : ""}`);
      else toast.error("No se pudo crear ninguna asignación");
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBulkSaving(false);
    }
  };

  const bulkCount = Object.keys(bulkEmps).filter((k) => bulkEmps[k]).length * bulkTargetDates().length;

  const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

  const setFsDay = (d, patch) => setFs((prev) => ({ ...prev, days: { ...prev.days, [d]: { ...prev.days[d], ...patch } } }));

  const saveFs = async () => {
    setFsSaving(true);
    try {
      await localApi.put("/fixed-schedule", { enabled: fs.enabled, tolerance_minutes: Number(fs.tolerance_minutes) || 0, days: fs.days });
      toast.success("Horario fijo guardado — las planillas ya pueden calcular HE y tardanzas desde asistencia");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setFsSaving(false);
    }
  };

  const weekLabel = `${format(weekStart, "d MMM", { locale: es })} – ${format(addDays(weekStart, 6), "d MMM yyyy", { locale: es })}`;

  return (
    <div data-testid="horarios-page">
      <PageHeader title="Horarios semanales" description="Asigna turnos a cada empleado por día" testid="horarios-header">
        <div className="flex items-center gap-2">
          <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="bulk-assign-button" onClick={openBulk}>
            <StackPlus className="mr-2" size={15} /> Asignación masiva
          </Button>
          <Button variant="outline" size="icon" className="rounded-sm border-input bg-transparent hover:bg-accent" data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}>
            <CaretLeft size={15} />
          </Button>
          <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground min-w-[180px]" data-testid="week-label"
            onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
            <CalendarBlank className="mr-2" size={15} /> {weekLabel}
          </Button>
          <Button variant="outline" size="icon" className="rounded-sm border-input bg-transparent hover:bg-accent" data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}>
            <CaretRight size={15} />
          </Button>
        </div>
      </PageHeader>

      {fs && (
        <div className="border border-border bg-card rounded-sm p-5 mb-4" data-testid="fixed-schedule-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-heading text-base font-bold text-foreground">Horario fijo semanal</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Entrada y salida por día. Con esto el sistema calcula automáticamente horas extra y llegadas tarde desde las marcaciones de asistencia, y las aplica en las planillas.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <Switch checked={!!fs.enabled} onCheckedChange={(v) => setFs({ ...fs, enabled: v })} data-testid="fs-enabled" />
                {fs.enabled ? "Activo" : "Inactivo"}
              </label>
              <Button size="sm" onClick={saveFs} disabled={fsSaving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="fs-save">
                {fsSaving && <CircleNotch className="animate-spin mr-2" size={14} />} Guardar horario fijo
              </Button>
            </div>
          </div>
          {fs.enabled && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-2 mt-4">
                {DAY_NAMES.map((name, i) => {
                  const day = (fs.days || {})[String(i)] || { works: false, entry: "08:00", exit: "17:00" };
                  return (
                    <div key={i} className={`border rounded-sm p-3 ${day.works ? "border-border" : "border-dashed border-border opacity-70"}`} data-testid={`fs-day-${i}`}>
                      <label className="flex items-center justify-between gap-2 text-sm text-foreground cursor-pointer">
                        <span className="font-medium">{name}</span>
                        <Switch checked={!!day.works} onCheckedChange={(v) => setFsDay(String(i), { works: v })} data-testid={`fs-day-${i}-works`} />
                      </label>
                      {day.works ? (
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-muted-foreground w-11">Entra</span>
                            <Input type="time" className={`${inputCls} h-8 text-xs`} value={day.entry} onChange={(e) => setFsDay(String(i), { entry: e.target.value })} data-testid={`fs-day-${i}-entry`} />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] text-muted-foreground w-11">Sale</span>
                            <Input type="time" className={`${inputCls} h-8 text-xs`} value={day.exit} onChange={(e) => setFsDay(String(i), { exit: e.target.value })} data-testid={`fs-day-${i}-exit`} />
                          </div>
                        </div>
                      ) : (
                        <p className="text-[11px] text-muted-foreground mt-2">Descanso</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-muted-foreground">Tolerancia de llegada tarde:</span>
                <Input type="number" min="0" className={`${inputCls} h-8 w-20`} value={fs.tolerance_minutes} onChange={(e) => setFs({ ...fs, tolerance_minutes: e.target.value })} data-testid="fs-tolerance" />
                <span className="text-xs text-muted-foreground">minutos</span>
              </div>
            </>
          )}
        </div>
      )}

      {!schedules ? (
        <Loading label="Cargando horarios..." />
      ) : employees.length === 0 ? (
        <EmptyState title="Sin empleados activos" description="Primero registra empleados para poder asignarles turnos." />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card" data-testid="schedule-grid">
          <table className="w-full text-sm border-collapse min-w-[900px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal sticky left-0 bg-card min-w-[180px]">Empleado</th>
                {days.map((d) => (
                  <th key={d.toISOString()} className="p-3 font-normal min-w-[130px]">
                    <div>{format(d, "EEE", { locale: es })}</div>
                    <div className="text-muted-foreground normal-case tracking-normal">{format(d, "d MMM", { locale: es })}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {employees.map((emp) => (
                <tr key={emp.id} data-testid={`schedule-row-${emp.employee_code}`}>
                  <td className="p-3 sticky left-0 bg-card border-r border-border">
                    <p className="text-foreground font-medium text-sm">{emp.full_name}</p>
                    <p className="text-xs text-muted-foreground">{emp.position || emp.employee_code}</p>
                  </td>
                  {days.map((d) => {
                    const a = byCell[`${emp.id}|${format(d, "yyyy-MM-dd")}`];
                    return (
                      <td key={d.toISOString()} className="p-1.5 align-top">
                        <button
                          onClick={() => openCell(emp, d)}
                          data-testid={`cell-${emp.employee_code}-${format(d, "yyyy-MM-dd")}`}
                          className={`w-full min-h-[64px] rounded-sm border text-left p-2 transition-colors duration-200 ${
                            a ? "border-input hover:border-foreground/40" : "border-dashed border-border hover:border-foreground/40"
                          } ${a?.shift_is_day_off ? "bg-muted/70" : "bg-transparent hover:bg-muted/70"}`}
                        >
                          {a ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: a.shift_color || "#0047FF" }} />
                                <span className="text-xs font-medium text-foreground truncate">{a.shift_name}</span>
                              </div>
                              {!a.shift_is_day_off && (
                                <p className="text-[11px] text-muted-foreground mt-1">{a.shift_start} – {a.shift_end}</p>
                              )}
                              {a.observation && <p className="text-[11px] text-amber-500/80 mt-1 truncate">{a.observation}</p>}
                            </>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">+ Asignar</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!cell} onOpenChange={() => setCell(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">
              {cell?.assignment ? "Editar asignación" : "Asignar turno"}
            </DialogTitle>
          </DialogHeader>
          {cell && (
            <div className="space-y-4" data-testid="assign-dialog">
              <p className="text-sm text-muted-foreground">
                {cell.employee.full_name} · <span className="text-foreground">{format(cell.date, "EEEE d 'de' MMMM", { locale: es })}</span>
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Turno</Label>
                <Select value={assignForm.shift_id} onValueChange={(v) => setAssignForm({ ...assignForm, shift_id: v })}>
                  <SelectTrigger className={inputCls} data-testid="assign-shift-select"><SelectValue placeholder="Selecciona un turno" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {shifts.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.name} {s.is_day_off ? "(descanso)" : `${s.start_time}–${s.end_time}`}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Observación</Label>
                <Input className={inputCls} data-testid="assign-observation-input" value={assignForm.observation}
                  onChange={(e) => setAssignForm({ ...assignForm, observation: e.target.value })} placeholder="Opcional" />
              </div>
              <DialogFooter className="flex gap-2">
                {cell.assignment && (
                  <Button type="button" variant="outline" onClick={removeCell} disabled={saving}
                    className="rounded-sm border-red-900 text-red-400 hover:bg-red-950/40 hover:text-red-300 mr-auto" data-testid="assign-delete-button">
                    <Trash className="mr-2" size={15} /> Quitar
                  </Button>
                )}
                <Button onClick={saveCell} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="assign-save-button">
                  {saving && <CircleNotch className="animate-spin mr-2" size={15} />}
                  Guardar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Asignación masiva de horarios</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="bulk-assign-dialog">
            <p className="text-xs text-muted-foreground">
              Aplica un turno a varios empleados y días de una vez. Para horarios fijos marca los días habituales; para rotativos,
              define el rango de la rotación (puedes repetir la operación por semana o grupo).
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Turno *</Label>
              <Select value={bulkForm.shift_id} onValueChange={(v) => setBulkForm({ ...bulkForm, shift_id: v })}>
                <SelectTrigger className={inputCls} data-testid="bulk-shift-select"><SelectValue placeholder="Selecciona un turno" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {shifts.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name} {s.is_day_off ? "(descanso)" : `${s.start_time}–${s.end_time}`}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Desde *</Label>
                <Input type="date" className={inputCls} data-testid="bulk-from" value={bulkRange.from} onChange={(e) => setBulkRange({ ...bulkRange, from: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Hasta *</Label>
                <Input type="date" className={inputCls} data-testid="bulk-to" value={bulkRange.to} onChange={(e) => setBulkRange({ ...bulkRange, to: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días de la semana</Label>
              <div className="flex flex-wrap gap-3 border border-border rounded-sm p-3">
                {WEEKDAYS.map(({ d, label }) => (
                  <label key={d} className="flex items-center gap-1.5 text-sm text-foreground cursor-pointer">
                    <Checkbox checked={!!bulkDays[d]} onCheckedChange={(c) => setBulkDays({ ...bulkDays, [d]: !!c })} className="border-input" data-testid={`bulk-day-${d}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleados ({Object.keys(bulkEmps).filter((k) => bulkEmps[k]).length} de {employees.length})</Label>
                <button type="button" className="text-xs text-primary hover:underline" data-testid="bulk-toggle-all"
                  onClick={() => {
                    const all = employees.every((e) => bulkEmps[e.id]);
                    setBulkEmps(Object.fromEntries(employees.map((e) => [e.id, !all])));
                  }}>
                  {employees.every((e) => bulkEmps[e.id]) ? "Quitar todos" : "Seleccionar todos"}
                </button>
              </div>
              <div className="border border-border rounded-sm divide-y divide-border max-h-44 overflow-y-auto">
                {employees.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 p-2.5 text-sm text-foreground/80 cursor-pointer hover:bg-muted/50">
                    <Checkbox checked={!!bulkEmps[e.id]} onCheckedChange={(c) => setBulkEmps({ ...bulkEmps, [e.id]: !!c })} className="border-input" data-testid={`bulk-emp-${e.employee_code}`} />
                    {e.full_name} <span className="text-xs text-muted-foreground">· {e.employee_code}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Observación</Label>
              <Input className={inputCls} placeholder="Opcional" data-testid="bulk-observation" value={bulkForm.observation} onChange={(e) => setBulkForm({ ...bulkForm, observation: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={bulkForm.overwrite} onCheckedChange={(v) => setBulkForm({ ...bulkForm, overwrite: v })} data-testid="bulk-overwrite" />
              Reemplazar asignaciones existentes en esas fechas
            </label>
            <div className="border border-border rounded-sm p-3 text-sm flex justify-between" data-testid="bulk-summary">
              <span className="text-muted-foreground">Total a crear</span>
              <span className="text-foreground font-bold">{bulkCount} asignación(es)</span>
            </div>
            <DialogFooter>
              <Button onClick={bulkSave} disabled={bulkSaving || !bulkForm.shift_id} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="bulk-save-button">
                {bulkSaving && <CircleNotch className="animate-spin mr-2" size={15} />} Aplicar asignación masiva
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
